import React, { memo, useState, useCallback } from 'react';
import { styled, alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { DateCalendar } from '@mui/x-date-pickers';
import clsx from 'clsx';
import {
  Divider,
  generateUtilityClass,
  unstable_composeClasses as composeClasses,
  generateUtilityClasses,
  useThemeProps,
} from '@mui/material';
import resolveComponentProps from '@mui/utils/resolveComponentProps';
import { useControlled } from '@mui/material/utils';
import { isBefore, isSameDay, isWithinInterval, isValid } from 'date-fns';
import { isNil } from 'lodash';

import { currentMonth, monthForward } from 'helpers/dates';
import Doppler from 'components/Doppler';
import { DateRangePickerDay } from './DateRangePickerDay';

const PREFIX = 'MuiDateRangePicker';

export function getDateRangePickerUtilityClass(slot) {
  return generateUtilityClass(PREFIX, slot);
}

const useUtilityClasses = (ownerState) => {
  const { classes } = ownerState;

  const slots = {
    root: ['root'],
    wrapper: ['wrapper'],
    wrapperStart: ['wrapperStart'],
    wrapperEnd: ['wrapperEnd'],
    wrapperSingle: ['wrapperSingle'],
    divider: ['divider'],
  };

  return composeClasses(slots, getDateRangePickerUtilityClass, classes);
};

const dateRangePickerClasses = generateUtilityClasses(PREFIX, [
  'root',
  'wrapper',
  'wrapperStart',
  'wrapperEnd',
  'wrapperSingle',
  'divider',
]);

const StyledComponent = styled(Doppler)(({ theme }) => ({
  display: 'inline-flex',
  backgroundColor: '#fff',
  padding: '20px 30px',

  '& .MuiPickersDay-day': {
    margin: '0',

    '&.MuiPickersDay-daySelected': {
      '&.MuiPickersDay-dayDisabled': {
        color: alpha(theme.palette.dark.contrastText, 0.5),
      },
    },
  },

  [`& .${dateRangePickerClasses.wrapper}`]: { flex: 1 },
  [`& .${dateRangePickerClasses.wrapperStart}`]: {},
  [`& .${dateRangePickerClasses.wrapperEnd}`]: {},
  [`& .${dateRangePickerClasses.wrapperSingle}`]: {},

  [`& .${dateRangePickerClasses.divider}`]: {
    margin: '0 30px',
    alignSelf: 'stretch',
    height: 'auto',
  },
}));

const CHOOSING_DATE_START = 'start';
const CHOOSING_DATE_END = 'end';

const DateRangePicker = memo(function DateRangePicker(inProps) {
  /**
   * MUI
   */
  const props = useThemeProps({ props: inProps, name: PREFIX });

  const {
    className,
    value: valueProp,
    onChange: onChangeProp,
    singleView,
    component: Component = 'div',
    slots,
    slotProps,
    ...rest
  } = props;

  const classes = useUtilityClasses(props);

  // Logic

  const [choosingDate, setChoosingDate] = useState(CHOOSING_DATE_START);

  const [[startDate, endDate], setValue] = useControlled({
    controlled: valueProp,
    default: [currentMonth, monthForward],
    state: 'dateRange',
    name: 'DateRangePicker',
  });

  const onStartChange = useCallback(
    (date) => {
      const v = [startDate, endDate];

      let result = [...v];

      const isDateAfter = !isNil(v[1]) && !isBefore(date, v[1]);

      if (isDateAfter) {
        result = [date, date];
      } else {
        result = [date, v[1]];
      }

      setValue(result);
      onChangeProp?.(result);
    },
    [endDate, onChangeProp, setValue, startDate],
  );

  const onEndChange = useCallback(
    (date) => {
      const v = [startDate, endDate];
      let result = [...v];

      const isDateBefore = isBefore(date, v[0]);

      if (isDateBefore) {
        result = [date, v[0]];
      } else {
        result = [v[0], date];
      }

      setValue(result);
      onChangeProp?.(result);
    },
    [endDate, onChangeProp, setValue, startDate],
  );

  const onSingleViewChange = useCallback(
    (date) => {
      if (choosingDate === CHOOSING_DATE_START) {
        onStartChange(date);
        setChoosingDate(CHOOSING_DATE_END);
      } else if (choosingDate === CHOOSING_DATE_END) {
        onEndChange(date);
        setChoosingDate(CHOOSING_DATE_START);
      }
    },
    [choosingDate, onStartChange, onEndChange],
  );

  const slotsForDayCalendar = {
    day: DateRangePickerDay,
    ...slots,
  };

  const slotPropsForDayCalendar = {
    ...slotProps,
    day: (dayOwnerState) => {
      const { day } = dayOwnerState;
      const isSelectedStartDate = isSameDay(day, startDate);
      const isSelectedEndDate = isSameDay(day, endDate);
      let dayInsideRangeInterval = false;

      if (
        isValid(startDate) &&
        isValid(endDate) &&
        isBefore(startDate, endDate)
      ) {
        try {
          dayInsideRangeInterval =
            isWithinInterval(day, {
              start: startDate,
              end: endDate,
            }) || isSelectedStartDate;
        } catch (e) {
          console.warn(e);
        }
      }

      const shouldHavePreview = false;

      let datePosition;
      if (isSelectedStartDate) {
        datePosition = 'start';
      } else if (isSelectedEndDate) {
        datePosition = 'end';
      }

      const isStartOfHighlighting = isSelectedStartDate;
      const isEndOfHighlighting = isSelectedEndDate;

      return {
        isPreviewing: shouldHavePreview ? dayInsideRangeInterval : false,
        isStartOfPreviewing: isStartOfHighlighting,
        isEndOfPreviewing: isEndOfHighlighting,
        isHighlighting: dayInsideRangeInterval,
        isStartOfHighlighting,
        isEndOfHighlighting,
        // apply selected styling to the dragging start or end day
        isVisuallySelected:
          dayOwnerState.selected ||
          isStartOfHighlighting ||
          isEndOfHighlighting,
        'data-position': datePosition,
        ...(resolveComponentProps(slotProps?.day, dayOwnerState) ?? {}),
      };
    },
  };

  return (
    <StyledComponent
      component={Component}
      className={clsx(classes.root, className)}
    >
      {!singleView && (
        <>
          <div className={clsx(classes.wrapper, classes.wrapperStart)}>
            <DateCalendar
              variant="static"
              value={startDate}
              onChange={onStartChange}
              slots={slotsForDayCalendar}
              slotProps={slotPropsForDayCalendar}
              data-picker="start"
              {...rest}
            />
          </div>

          <Divider className={classes.divider} orientation="vertical" />

          <div className={clsx(classes.wrapper, classes.wrapperEnd)}>
            <DateCalendar
              variant="static"
              value={endDate}
              onChange={onEndChange}
              slots={slotsForDayCalendar}
              slotProps={slotPropsForDayCalendar}
              data-picker="end"
              {...rest}
            />
          </div>
        </>
      )}

      {singleView && (
        <div className={clsx(classes.wrapper, classes.wrapperSingle)}>
          <DateCalendar
            variant="static"
            value={choosingDate === CHOOSING_DATE_START ? startDate : endDate}
            onChange={onSingleViewChange}
            slots={slotsForDayCalendar}
            slotProps={slotPropsForDayCalendar}
            data-picker="single"
            {...rest}
          />
        </div>
      )}
    </StyledComponent>
  );
});

// eslint-disable-next-line no-unused-vars
const createRenderDay =
  (type, { startDate, endDate, singleView, choosingDate, classes }) =>
  (thisDayDate, selectedDate, isDayInThisMonth, nativeDayArg) => {
    const isStartDay = isSameDay(thisDayDate, startDate);
    const isEndDay = isSameDay(thisDayDate, endDate);

    const isEmptyEndDay = type === 'end' && !endDate;
    const isEmptyStartDay = type === 'start' && !startDate;

    let isDayBetween;

    if (
      isValid(startDate) &&
      isValid(endDate) &&
      isBefore(startDate, endDate)
    ) {
      try {
        isDayBetween =
          isWithinInterval(thisDayDate, {
            start: startDate,
            end: endDate,
          }) || isStartDay;
      } catch (e) {
        console.warn(e);
      }
    }

    const isSelected =
      !isEmptyStartDay &&
      !isEmptyEndDay &&
      (nativeDayArg.selected || isStartDay || isEndDay);
    const nativeDay = React.cloneElement(nativeDayArg, {
      selected: isSelected,
    });

    const wrapperClasses = clsx(classes.dayWrapper, {
      [classes.isSelected]: isSelected,
      [classes.isDayBetween]: isDayBetween,

      [classes.isStartDay]: isStartDay,
      [classes.isEndDay]: isEndDay,
      [classes.isSelectingSingleView]:
        singleView &&
        ((isStartDay && choosingDate === CHOOSING_DATE_START) ||
          (isEndDay && choosingDate === CHOOSING_DATE_END)),
      [classes.isDayOutOfThisMonth]: !isDayInThisMonth,
    });

    return (
      // eslint-disable-next-line react/destructuring-assignment
      <div className={wrapperClasses} data-day={thisDayDate.getDate()}>
        {nativeDay}
      </div>
    );
  };

DateRangePicker.propTypes = {
  component: PropTypes.node,
  className: PropTypes.string,
  classes: PropTypes.object,
  slots: PropTypes.object,
  slotProps: PropTypes.object,
  value: PropTypes.array,
  onChange: PropTypes.func,
  singleView: PropTypes.bool,
};

export default DateRangePicker;
